/**
 * 📊 DASHBOARD AFFILIATION ADMIN COMPLET
 * Suivi très précis et détaillé du système d'affiliation Heartbeats
 * Pour SupremeAdminPage (g13)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, DollarSign, TrendingUp, Link2, MousePointer,
  Award, CheckCircle, XCircle, Clock, ArrowUpRight, ArrowDownRight,
  RefreshCw, Eye, Download, Filter, Search, X,
  ChevronRight, Wallet, CreditCard, Building2, AlertTriangle,
  BarChart3, PieChart, Activity, Globe, Gift, Target,
  UserPlus, Percent, Calendar, TrendingDown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 📊 Carte Stat avec tendance
const StatCard = ({ icon: Icon, label, value, subValue, color, trend, onClick }) => (
  <motion.div
    whileHover={onClick ? { scale: 1.02 } : {}}
    onClick={onClick}
    className={`rounded-xl p-4 border border-${color}-500/30 bg-gradient-to-br from-${color}-900/20 to-transparent ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className={`p-2 rounded-lg bg-${color}-500/20`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      {trend !== undefined && trend !== null && (
        <span className={`text-xs flex items-center gap-1 px-2 py-1 rounded-full ${
          trend > 0 ? 'bg-green-500/20 text-green-400' : 
          trend < 0 ? 'bg-red-500/20 text-red-400' : 
          'bg-gray-500/20 text-gray-400'
        }`}>
          {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : trend < 0 ? <ArrowDownRight className="w-3 h-3" /> : null}
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-gray-400 text-sm">{label}</p>
    {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
  </motion.div>
);

// 📈 Mini graphique en barres
const MiniBarChart = ({ data, height = 60 }) => {
  const maxValue = Math.max(...data.map(d => d.visits + d.conversions), 1);
  
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
          <div className="w-full flex flex-col gap-0.5">
            <div 
              className="w-full bg-pink-500 rounded-t"
              style={{ height: Math.max(2, (d.visits / maxValue) * (height - 20)) }}
              title={`${d.visits} visites`}
            />
            <div 
              className="w-full bg-green-500 rounded-b"
              style={{ height: Math.max(2, (d.conversions / maxValue) * (height - 20)) }}
              title={`${d.conversions} conversions`}
            />
          </div>
          <span className="text-[10px] text-gray-500">{d.date}</span>
        </div>
      ))}
    </div>
  );
};

// 🏆 Ligne Top Affilié
const TopAffiliateRow = ({ affiliate, rank }) => (
  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
      rank === 1 ? 'bg-yellow-500 text-black' :
      rank === 2 ? 'bg-gray-400 text-black' :
      rank === 3 ? 'bg-amber-700 text-white' :
      'bg-gray-700 text-gray-300'
    }`}>
      {rank}
    </div>
    <div className="flex-1">
      <p className="text-white font-medium">{affiliate.name || 'Anonyme'}</p>
      <p className="text-pink-400 text-xs font-mono">{affiliate.code}</p>
    </div>
    <div className="text-right">
      <p className="text-green-400 font-bold">{affiliate.earned.toFixed(2)}€</p>
      <p className="text-gray-500 text-xs">{affiliate.referrals} filleuls</p>
    </div>
  </div>
);

// 💸 Ligne Retrait en attente
const PendingWithdrawalRow = ({ withdrawal, onProcess }) => (
  <div className="flex items-center justify-between p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
    <div className="flex items-center gap-3">
      <Wallet className="w-5 h-5 text-yellow-400" />
      <div>
        <p className="text-white font-bold">{withdrawal.amount.toFixed(2)}€</p>
        <p className="text-gray-500 text-xs">
          via {withdrawal.method === 'paypal' ? 'PayPal' : withdrawal.method === 'iban' ? 'Virement' : 'Crédit'}
        </p>
      </div>
    </div>
    <div className="flex gap-2">
      <Button
        size="sm"
        onClick={() => onProcess(withdrawal.id, 'approve')}
        className="bg-green-500 hover:bg-green-600 text-white"
      >
        <CheckCircle className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        onClick={() => onProcess(withdrawal.id, 'reject')}
        className="bg-red-500 hover:bg-red-600 text-white"
      >
        <XCircle className="w-4 h-4" />
      </Button>
    </div>
  </div>
);

// 🔔 Alerte
const AlertBox = ({ alert }) => (
  <div className={`flex items-center gap-3 p-3 rounded-xl border ${
    alert.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
    alert.type === 'error' ? 'bg-red-500/10 border-red-500/30' :
    'bg-blue-500/10 border-blue-500/30'
  }`}>
    <AlertTriangle className={`w-5 h-5 ${
      alert.type === 'warning' ? 'text-yellow-400' :
      alert.type === 'error' ? 'text-red-400' :
      'text-blue-400'
    }`} />
    <p className="text-white text-sm flex-1">{alert.message}</p>
  </div>
);

// 📊 COMPOSANT PRINCIPAL
export default function AffiliationCompleteAdminDashboard({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [affiliatesList, setAffiliatesList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Charger les données
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/affiliation/admin/complete-stats`);
      const result = await response.json();
      if (result.success) {
        setData(result);
      } else {
        toast.error('Erreur chargement données');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger la liste des affiliés
  const loadAffiliates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/affiliation/admin/affiliates-list?search=${searchQuery}`);
      const result = await response.json();
      if (result.success) {
        setAffiliatesList(result.affiliates);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  }, [searchQuery]);

  // Traiter un retrait
  const processWithdrawal = async (withdrawalId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/affiliation/admin/process-withdrawal/${withdrawalId}?action=${action}`, {
        method: 'POST'
      });
      const result = await response.json();
      if (result.success) {
        toast.success(action === 'approve' ? 'Retrait approuvé !' : 'Retrait rejeté');
        loadData();
      } else {
        toast.error(result.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de traitement');
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'affiliates') {
      loadAffiliates();
    }
  }, [activeTab, loadAffiliates]);

  if (loading || !data) {
    return (
      <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-pink-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  const { stats, alerts } = data;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 overflow-y-auto"
    >
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500">
                <Link2 className="w-6 h-6" />
              </div>
              Dashboard Affiliation Heartbeats
            </h1>
            <p className="text-gray-500 mt-1">
              Dernière mise à jour: {new Date(data.generatedAt).toLocaleString('fr-FR')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={loadData}
              className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="ghost" onClick={onClose} className="text-gray-400">
              ✕
            </Button>
          </div>
        </div>

        {/* Alertes */}
        {alerts && alerts.length > 0 && (
          <div className="mb-6 space-y-2">
            {alerts.map((alert, idx) => (
              <AlertBox key={idx} alert={alert} />
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', icon: BarChart3, label: 'Vue d\'ensemble' },
            { id: 'finances', icon: DollarSign, label: 'Finances' },
            { id: 'tracking', icon: MousePointer, label: 'Tracking' },
            { id: 'affiliates', icon: Users, label: 'Affiliés' },
            { id: 'withdrawals', icon: Wallet, label: 'Retraits' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-pink-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats principales */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Affiliés actifs"
                value={stats.affiliates.active}
                subValue={`+${stats.affiliates.new30d} ce mois`}
                color="pink"
              />
              <StatCard
                icon={DollarSign}
                label="Commissions totales"
                value={`${stats.finances.totalEarned.toFixed(2)}€`}
                subValue={`${stats.finances.commissions30d.toFixed(2)}€ ce mois`}
                color="green"
              />
              <StatCard
                icon={UserPlus}
                label="Filleuls N1"
                value={stats.referrals.N1}
                subValue={`Total: ${stats.referrals.total}`}
                color="purple"
              />
              <StatCard
                icon={Percent}
                label="Taux conversion"
                value={`${stats.tracking.conversionRate}%`}
                subValue={`${stats.tracking.totalConversions} conversions`}
                color="cyan"
              />
            </div>

            {/* Graphique évolution 7j */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl p-6 border border-white/10 bg-white/5">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-pink-400" />
                  Évolution 7 derniers jours
                </h3>
                <MiniBarChart data={stats.evolution7d} height={100} />
                <div className="flex justify-center gap-6 mt-4 text-xs">
                  <span className="flex items-center gap-1 text-pink-400">
                    <span className="w-3 h-3 bg-pink-500 rounded" /> Visites
                  </span>
                  <span className="flex items-center gap-1 text-green-400">
                    <span className="w-3 h-3 bg-green-500 rounded" /> Conversions
                  </span>
                </div>
              </div>

              {/* Top affiliés */}
              <div className="rounded-xl p-6 border border-white/10 bg-white/5">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-400" />
                  Top 5 Affiliés
                </h3>
                <div className="space-y-2">
                  {stats.topAffiliates.slice(0, 5).map((affiliate, idx) => (
                    <TopAffiliateRow key={affiliate.id} affiliate={affiliate} rank={idx + 1} />
                  ))}
                  {stats.topAffiliates.length === 0 && (
                    <p className="text-gray-500 text-center py-4">Aucun affilié pour le moment</p>
                  )}
                </div>
              </div>
            </div>

            {/* Répartition des filleuls par niveau */}
            <div className="rounded-xl p-6 border border-white/10 bg-white/5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                Répartition des filleuls par niveau
              </h3>
              <div className="grid grid-cols-4 gap-4">
                {['N1', 'N2', 'N3', 'N4'].map((level, idx) => (
                  <div key={level} className="text-center p-4 rounded-xl bg-white/5">
                    <p className={`text-3xl font-bold ${
                      idx === 0 ? 'text-pink-400' :
                      idx === 1 ? 'text-purple-400' :
                      idx === 2 ? 'text-blue-400' :
                      'text-cyan-400'
                    }`}>
                      {stats.referrals[level]}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">Niveau {idx + 1}</p>
                    <p className="text-gray-600 text-xs">
                      {idx === 0 ? '10%' : idx === 1 ? '5%' : idx === 2 ? '3%' : '2%'} commission
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab: Finances */}
        {activeTab === 'finances' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={DollarSign}
                label="Total gagné"
                value={`${stats.finances.totalEarned.toFixed(2)}€`}
                color="green"
              />
              <StatCard
                icon={Wallet}
                label="Total retiré"
                value={`${stats.finances.totalWithdrawn.toFixed(2)}€`}
                color="blue"
              />
              <StatCard
                icon={Clock}
                label="En attente"
                value={`${stats.finances.totalPending}€`}
                color="yellow"
              />
              <StatCard
                icon={Calendar}
                label="Commissions 30j"
                value={`${stats.finances.commissions30d.toFixed(2)}€`}
                color="purple"
              />
            </div>

            {/* Retraits en attente */}
            {stats.pendingWithdrawals.length > 0 && (
              <div className="rounded-xl p-6 border border-yellow-500/30 bg-yellow-500/10">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  {stats.pendingWithdrawals.length} Retrait(s) en attente
                </h3>
                <div className="space-y-2">
                  {stats.pendingWithdrawals.map(withdrawal => (
                    <PendingWithdrawalRow
                      key={withdrawal.id}
                      withdrawal={withdrawal}
                      onProcess={processWithdrawal}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab: Tracking */}
        {activeTab === 'tracking' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={MousePointer}
                label="Total visites"
                value={stats.tracking.totalVisits}
                color="pink"
              />
              <StatCard
                icon={UserPlus}
                label="Conversions"
                value={stats.tracking.totalConversions}
                color="green"
              />
              <StatCard
                icon={Clock}
                label="Tracking actifs"
                value={stats.tracking.activeTrackings}
                subValue="Cookies 90j en cours"
                color="blue"
              />
              <StatCard
                icon={Percent}
                label="Taux conversion"
                value={`${stats.tracking.conversionRate}%`}
                color="purple"
              />
            </div>

            {/* Top sources */}
            <div className="rounded-xl p-6 border border-white/10 bg-white/5">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-400" />
                Top Sources de trafic
              </h3>
              {stats.topSources.length > 0 ? (
                <div className="space-y-2">
                  {stats.topSources.map((source, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {source.source === 'instagram' ? '📸' :
                           source.source === 'tiktok' ? '🎵' :
                           source.source === 'whatsapp' ? '💬' :
                           source.source === 'direct' ? '🔗' : '🌐'}
                        </span>
                        <span className="text-white font-medium capitalize">{source.source}</span>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-pink-400 font-bold">{source.visits}</p>
                          <p className="text-gray-500 text-xs">visites</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold">{source.conversions}</p>
                          <p className="text-gray-500 text-xs">conversions</p>
                        </div>
                        <div className="text-right">
                          <p className="text-purple-400 font-bold">
                            {source.visits > 0 ? ((source.conversions / source.visits) * 100).toFixed(1) : 0}%
                          </p>
                          <p className="text-gray-500 text-xs">taux</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée de tracking pour le moment</p>
              )}
            </div>
          </div>
        )}

        {/* Tab: Affiliés */}
        {activeTab === 'affiliates' && (
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  placeholder="Rechercher un affilié..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              {affiliatesList.map(affiliate => (
                <div key={affiliate.id} className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {affiliate.first_name?.[0] || 'A'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{affiliate.first_name} {affiliate.last_name}</p>
                      <p className="text-pink-400 text-sm font-mono">{affiliate.affiliate_code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-green-400 font-bold">{affiliate.stats?.total_earned?.toFixed(2) || '0.00'}€</p>
                      <p className="text-gray-500 text-xs">Gagné</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white">{affiliate.stats?.total_referrals?.N1 || 0}</p>
                      <p className="text-gray-500 text-xs">Filleuls</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      affiliate.status === 'active' ? 'bg-green-500/20 text-green-400' :
                      affiliate.status === 'blacklisted' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {affiliate.status}
                    </span>
                  </div>
                </div>
              ))}
              {affiliatesList.length === 0 && (
                <p className="text-gray-500 text-center py-8">Aucun affilié trouvé</p>
              )}
            </div>
          </div>
        )}

        {/* Tab: Retraits */}
        {activeTab === 'withdrawals' && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <StatCard
                icon={Wallet}
                label="En attente"
                value={stats.finances.pendingWithdrawals}
                subValue={`${stats.finances.pendingWithdrawalAmount.toFixed(2)}€`}
                color="yellow"
              />
              <StatCard
                icon={CheckCircle}
                label="Total retiré"
                value={`${stats.finances.totalWithdrawn.toFixed(2)}€`}
                color="green"
              />
              <StatCard
                icon={DollarSign}
                label="Solde total affiliés"
                value={`${stats.finances.totalPending}€`}
                color="blue"
              />
            </div>

            {stats.pendingWithdrawals.length > 0 ? (
              <div className="space-y-2">
                <h3 className="text-white font-bold mb-4">Retraits à traiter</h3>
                {stats.pendingWithdrawals.map(withdrawal => (
                  <PendingWithdrawalRow
                    key={withdrawal.id}
                    withdrawal={withdrawal}
                    onProcess={processWithdrawal}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-white text-lg">Tous les retraits ont été traités !</p>
                <p className="text-gray-500">Aucun retrait en attente</p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 text-center">
          <p className="text-gray-400 text-sm">
            💡 Les commissions sont validées automatiquement après 30 jours d'abonnement actif
          </p>
        </div>
      </div>
    </motion.div>
  );
}
